import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { bookingService } from '../services/api';
import { FiCheckCircle, FiCalendar, FiMapPin, FiDollarSign, FiCopy, FiHome, FiLoader } from 'react-icons/fi';
import { formatDateLong } from '../utils/date';
import './PaymentSuccess.css';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resetBooking } = useBooking();
  const { booking_id, payment_id } = location.state || {};
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 600);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!booking_id) {
        setError('Booking ID not found');
        setLoading(false);
        return;
      }

      try {
        const response = await bookingService.getById(booking_id);
        setBooking(response.data);
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError('Failed to fetch booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [booking_id]);

  const handleNewBooking = () => {
    resetBooking();
    navigate('/');
  };

  const copyBookingId = () => {
    if (booking?.booking_id) {
      navigator.clipboard.writeText(booking.booking_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="payment-success">
        <div className="container">
          <div className="loading-state">
            <FiLoader className="loading-spinner" />
            <h2>Verifying Payment...</h2>
            <p>Please wait while we confirm your payment</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="payment-success">
        <div className="container">
          <div className="error-state">
            <h1>Error</h1>
            <p>{error || 'Booking not found'}</p>
            <button className="primary-button" onClick={handleNewBooking}>
              <FiHome />
              <span>Go to Home</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success">
      <div className="container">
        <div className={`success-card ${isAnimating ? 'animating' : ''}`}>
          <div className="success-animation">
            <div className="success-icon-wrapper">
              <FiCheckCircle className="success-icon" />
            </div>
            <div className="success-ripple"></div>
            <div className="success-ripple delay-1"></div>
            <div className="success-ripple delay-2"></div>
          </div>

          <h1 className="success-title">Payment Successful!</h1>
          <p className="success-subtitle">
            Your booking has been confirmed. We've sent the details to your email.
          </p>

          {payment_id && (
            <div className="payment-id-section">
              <div className="payment-id-label">Payment ID</div>
              <div className="payment-id-value">{payment_id}</div>
            </div>
          )}

          <div className="booking-id-section">
            <div className="booking-id-label">Booking ID</div>
            <div className="booking-id-wrapper">
              <span className="booking-id">{booking.booking_id}</span>
              <button className="copy-button" onClick={copyBookingId} title="Copy Booking ID">
                <FiCopy />
                {copied && <span className="copied-tooltip">Copied!</span>}
              </button>
            </div>
          </div>

          <div className="booking-details-card">
            <h2 className="details-title">Booking Details</h2>

            <div className="details-grid">
              <div className="detail-card">
                <div className="detail-icon location">
                  <FiMapPin />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">{booking.location?.name || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon date">
                  <FiCalendar />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Visit Date</span>
                  <span className="detail-value">{formatDateLong(booking.visit_date)}</span>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon amount">
                  <FiDollarSign />
                </div>
                <div className="detail-content">
                  <span className="detail-label">Total Amount</span>
                  <span className="detail-value amount-value">
                    ₹ {parseFloat(booking.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="next-steps">
            <h3 className="next-steps-title">What's Next?</h3>
            <ul className="steps-list">
              <li>
                <span className="step-number">1</span>
                <span>Check your email for booking confirmation and ticket details</span>
              </li>
              <li>
                <span className="step-number">2</span>
                <span>Arrive at the park on your selected date with a valid ID</span>
              </li>
              <li>
                <span className="step-number">3</span>
                <span>Present your booking ID at the entrance to receive your tickets</span>
              </li>
            </ul>
          </div>

          <div className="action-buttons">
            <button className="secondary-button" onClick={() => window.print()}>
              <span>Print Details</span>
            </button>
            <button className="primary-button" onClick={handleNewBooking}>
              <FiHome />
              <span>Book Another Ticket</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
